import { Helmet } from "react-helmet-async";
import { withTrailingSlash } from "../../constants/routes";

export const SITE_URL = "https://listino.ai";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/listino-logo.png`;

/**
 * Per-page SEO/GEO tags: title, meta description, trailing-slash canonical,
 * Open Graph, Twitter card, optional robots noindex, and optional JSON-LD
 * structured data.
 *
 * Rendered via react-helmet-async. At build time react-snap visits each public
 * route and snapshots the rendered DOM (including whatever Helmet flushed), so
 * these tags end up in the static HTML crawlers and AI engines receive.
 *
 * Canonicals are trailing-slash ("/faq/") to match how S3/CloudFront serves the
 * prerendered files. noindex pages point their canonical at the site root.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {string} [props.description]
 * @param {string} [props.path]   Canonical path, e.g. "/pricing"
 * @param {string} [props.image]  Absolute OG image URL
 * @param {boolean} [props.noindex]
 * @param {object | object[]} [props.jsonLd]  schema.org object(s)
 * @param {number} [props.prerenderStatusCode]  HTTP status react-snap should
 *   record for this page (e.g. 404 on the not-found page), via the
 *   `prerender-status-code` meta tag.
 */
const Seo = ({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd,
  prerenderStatusCode,
}) => {
  const canonical = noindex
    ? `${SITE_URL}/`
    : path
      ? `${SITE_URL}${withTrailingSlash(path)}`
      : undefined;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <>
      <Helmet prioritizeSeoTags>
        {title && <title>{title}</title>}
        {description && <meta name="description" content={description} />}
        {canonical && <link rel="canonical" href={canonical} />}
        {noindex && <meta name="robots" content="noindex, follow" />}
        {prerenderStatusCode && (
          <meta name="prerender-status-code" content={String(prerenderStatusCode)} />
        )}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Listino" />
        {title && <meta property="og:title" content={title} />}
        {description && <meta property="og:description" content={description} />}
        {canonical && <meta property="og:url" content={canonical} />}
        <meta property="og:image" content={image} />

        <meta name="twitter:card" content="summary_large_image" />
        {title && <meta name="twitter:title" content={title} />}
        {description && <meta name="twitter:description" content={description} />}
        <meta name="twitter:image" content={image} />
      </Helmet>

      {/* JSON-LD is rendered as real DOM (not via Helmet) so the build-time
          prerenderer (react-snap) reliably captures it — helmet-injected
          <script> tags are not snapshotted. Search engines read JSON-LD from
          anywhere in the document. */}
      {blocks.map((block, i) => (
        <script
          // biome-ignore lint/suspicious/noArrayIndexKey: static, order-stable list
          key={i}
          type="application/ld+json"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted first-party JSON-LD
          dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
        />
      ))}
    </>
  );
};

export default Seo;
